/**
 * Slack API Emulation Plugin
 *
 * Emulates the Slack Web API for sandboxed apps so customers can test their
 * Slack integration end-to-end without a real workspace:
 *
 * - Sandbox-side: OAuth token exchange, auth.test, conversations, chat.postMessage
 *   (recorded into state for assertions), users/team info.
 * - Browser-side: intercepts the slack.com/oauth/v2/authorize redirect so the
 *   connect flow completes headlessly.
 * - Inbound events: builds signed `event_callback` webhooks (app_mention,
 *   message, reaction_added) that the control plane fires INTO the app. The
 *   HMAC scheme is byte-compatible with Slack's `v0=` signing, so the app's
 *   production signature verification passes using the shared signing secret
 *   from the env contract.
 *
 * Response shapes mirror the zod schemas in @chatsdet/slack (types.ts) —
 * conformance is asserted in slack.test.ts.
 *
 * @module
 */

import crypto from 'node:crypto';
import { z } from 'zod';
import type {
  ActionInterfaceDef,
  EmulationPlugin,
  EmulationRequest,
  EmulationResponse,
  InboundEventDef,
  PluginContext,
} from '../plugin.js';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SlackEmuConfig {
  /** Override the app_id reported by oauth.v2.access */
  appId?: string;
}

const SlackEmuChannelSchema = z.object({
  id: z.string(),
  name: z.string(),
  is_private: z.boolean().optional(),
  is_member: z.boolean().optional(),
  is_archived: z.boolean().optional(),
});

const SlackEmuUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  real_name: z.string().optional(),
  email: z.string().optional(),
  is_bot: z.boolean().optional(),
});

export const slackFixtureSchema = z.object({
  team: z.object({
    id: z.string(),
    name: z.string(),
    domain: z.string().optional(),
  }),
  botUserId: z.string(),
  channels: z.array(SlackEmuChannelSchema),
  users: z.array(SlackEmuUserSchema),
});

export type SlackEmuChannel = z.infer<typeof SlackEmuChannelSchema>;
export type SlackEmuUser = z.infer<typeof SlackEmuUserSchema>;
export type SlackFixture = z.infer<typeof slackFixtureSchema>;

export interface PostedMessage {
  channel: string;
  text: string;
  ts: string;
  thread_ts?: string;
  user: string;
  blocks?: unknown;
  attachments?: unknown;
}

export interface SlackState {
  team: SlackFixture['team'];
  botUserId: string;
  appId: string;
  channels: Map<string, SlackEmuChannel>;
  users: Map<string, SlackEmuUser>;
  /** Messages the app posted via chat.postMessage — the assertion surface */
  postedMessages: PostedMessage[];
  botToken: string;
  tsCounter: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const EMULATED_CODE_PREFIX = 'emulated_code_';
const DEFAULT_APP_ID = 'A_EMULATED';

/** Mirrors SLACK_BOT_SCOPES in @chatsdet/slack (kept local — runtime dep is dev-only) */
const EMULATED_BOT_SCOPES =
  'chat:write,channels:read,groups:read,team:read,app_mentions:read,channels:history,groups:history,reactions:read,users:read,users:read.email,files:write';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

type SlackCtx = PluginContext<SlackFixture, SlackState>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- parsing untrusted API input
function safeParseJson(body: string | undefined): { ok: true; data: any } | { ok: false; error: EmulationResponse } {
  try {
    return { ok: true, data: JSON.parse(body ?? '{}') };
  } catch {
    return { ok: false, error: slackError('invalid_json') };
  }
}

/** Slack returns HTTP 200 with `{ok:false, error}` for API-level failures. */
function slackError(error: string): EmulationResponse {
  return { status: 200, body: { ok: false, error } };
}

/**
 * Enforce bot-token auth the way Slack does: bearer must match the token
 * minted at provision. Returns null when authorized.
 */
function requireBot(req: EmulationRequest, ctx: SlackCtx): EmulationResponse | null {
  const auth = req.headers['authorization'] ?? '';
  const token = auth.startsWith('Bearer ') ? auth.slice('Bearer '.length).trim() : '';
  if (!token) return slackError('not_authed');
  if (token !== ctx.state.botToken) return slackError('invalid_auth');
  return null;
}

/** Mint a Slack-style message ts: unix seconds + monotonic counter suffix. */
function mintTs(state: SlackState): string {
  const seconds = Math.floor(Date.now() / 1000);
  state.tsCounter += 1;
  return `${seconds}.${String(state.tsCounter).padStart(6, '0')}`;
}

/** Read a param from query string (GET) or JSON body (POST). */
function readParam(req: EmulationRequest, key: string): string | undefined {
  const fromQuery = req.url.searchParams.get(key);
  if (fromQuery !== null) return fromQuery;
  const parsed = safeParseJson(req.body);
  if (!parsed.ok) return undefined;
  const value = parsed.data[key];
  return value === undefined || value === null ? undefined : String(value);
}

function signSlackRequest(signingSecret: string, timestamp: string, body: string): string {
  const basestring = `v0:${timestamp}:${body}`;
  return `v0=${crypto.createHmac('sha256', signingSecret).update(basestring).digest('hex')}`;
}

/** Build a signed event_callback request aimed at the app's webhook path. */
function buildSignedEvent(opts: {
  env: Record<string, string>;
  targetPath: string;
  teamId: string;
  event: Record<string, unknown>;
}): ReturnType<InboundEventDef['build']> {
  const body = JSON.stringify({
    type: 'event_callback',
    token: 'emulated-verification-token',
    team_id: opts.teamId,
    event: opts.event,
    event_id: `Ev${crypto.randomUUID().replaceAll('-', '').slice(0, 16).toUpperCase()}`,
  });
  // Timestamp is minted at fire time so the app's 5-minute window holds.
  const timestamp = String(Math.floor(Date.now() / 1000));
  const signingSecret = opts.env.signingSecret ?? '';
  return {
    method: 'POST',
    path: opts.targetPath,
    headers: {
      'content-type': 'application/json',
      'x-slack-request-timestamp': timestamp,
      'x-slack-signature': signSlackRequest(signingSecret, timestamp, body),
    },
    body,
  };
}

function teamIdFrom(fixture: unknown, override?: string): string {
  if (override) return override;
  const parsed = slackFixtureSchema.safeParse(fixture);
  return parsed.success ? parsed.data.team.id : 'T_EMULATED';
}

function nowEventTs(): string {
  return `${Math.floor(Date.now() / 1000)}.000001`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Inbound events
// ─────────────────────────────────────────────────────────────────────────────

const appMentionParamsSchema = z.object({
  channel: z.string(),
  user: z.string(),
  text: z.string(),
  ts: z.string().optional(),
  thread_ts: z.string().optional(),
  team_id: z.string().optional(),
});

const messageParamsSchema = z.object({
  channel: z.string(),
  user: z.string(),
  text: z.string(),
  thread_ts: z.string().optional(),
  ts: z.string().optional(),
  channel_type: z.enum(['channel', 'group', 'im', 'mpim']).optional(),
  team_id: z.string().optional(),
});

const reactionAddedParamsSchema = z.object({
  user: z.string(),
  reaction: z.string(),
  channel: z.string(),
  message_ts: z.string(),
  item_user: z.string().optional(),
  team_id: z.string().optional(),
});

const inboundEvents: InboundEventDef[] = [
  {
    id: 'app-mention',
    description: 'A user @-mentions the app in a channel (signed app_mention event_callback)',
    paramsSchema: appMentionParamsSchema,
    build: ({ params, env, targetPath, fixture }) => {
      const p = appMentionParamsSchema.parse(params);
      return buildSignedEvent({
        env,
        targetPath,
        teamId: teamIdFrom(fixture, p.team_id),
        event: {
          type: 'app_mention',
          user: p.user,
          text: p.text,
          channel: p.channel,
          ts: p.ts ?? nowEventTs(),
          ...(p.thread_ts ? { thread_ts: p.thread_ts } : {}),
        },
      });
    },
  },
  {
    id: 'message',
    description: 'A user posts a (thread) message in a channel (signed message event_callback)',
    paramsSchema: messageParamsSchema,
    build: ({ params, env, targetPath, fixture }) => {
      const p = messageParamsSchema.parse(params);
      return buildSignedEvent({
        env,
        targetPath,
        teamId: teamIdFrom(fixture, p.team_id),
        event: {
          type: 'message',
          user: p.user,
          text: p.text,
          channel: p.channel,
          ts: p.ts ?? nowEventTs(),
          ...(p.thread_ts ? { thread_ts: p.thread_ts } : {}),
          ...(p.channel_type ? { channel_type: p.channel_type } : {}),
        },
      });
    },
  },
  {
    id: 'reaction-added',
    description: 'A user reacts to a message (signed reaction_added event_callback)',
    paramsSchema: reactionAddedParamsSchema,
    build: ({ params, env, targetPath, fixture }) => {
      const p = reactionAddedParamsSchema.parse(params);
      return buildSignedEvent({
        env,
        targetPath,
        teamId: teamIdFrom(fixture, p.team_id),
        event: {
          type: 'reaction_added',
          user: p.user,
          reaction: p.reaction,
          item: { type: 'message', channel: p.channel, ts: p.message_ts },
          ...(p.item_user ? { item_user: p.item_user } : {}),
          event_ts: nowEventTs(),
        },
      });
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Action interfaces
// ─────────────────────────────────────────────────────────────────────────────

const actionInterfaces: ActionInterfaceDef[] = [
  {
    slug: 'slack-seed-data',
    label: 'Seed Slack data',
    description:
      'Replace the emulated Slack workspace fixture (team, channels, users). Idempotent — re-running resets to exactly the given data.',
    inputSchema: {
      type: 'object',
      properties: {
        fixture: {
          type: 'string',
          description: 'JSON fixture: { team, botUserId, channels, users }',
        },
      },
      required: ['fixture'],
    },
    outputSchema: {
      type: 'object',
      properties: { ok: { type: 'boolean' } },
    },
    scaffoldScript: `# Replace the emulated Slack workspace data.
# $INPUT_FIXTURE is the JSON fixture; the runner exports EMULATION_BASE_URL
# and EMULATION_CONTROL_TOKEN automatically when emulation is enabled.
curl -sf -X POST "$EMULATION_BASE_URL/plugins/slack/fixture" \\
  -H "Authorization: Bearer $EMULATION_CONTROL_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d "{\\"fixture\\": $INPUT_FIXTURE}"`,
  },
  {
    slug: 'slack-fire-event',
    label: 'Fire a Slack event into the app',
    description:
      "Send a signed Slack webhook (app-mention | message | reaction-added) into the app's Slack events endpoint. Set the target path to YOUR app's webhook route.",
    inputSchema: {
      type: 'object',
      properties: {
        event: {
          type: 'string',
          enum: ['app-mention', 'message', 'reaction-added'],
          description: 'Inbound event id',
        },
        params: { type: 'string', description: 'JSON params for the event (channel, user, text, …)' },
      },
      required: ['event', 'params'],
    },
    outputSchema: {
      type: 'object',
      properties: {
        ok: { type: 'boolean' },
        status: { type: 'number' },
      },
    },
    scaffoldScript: `# Fire a signed Slack event into the app.
# EDIT the targetPath to your app's Slack webhook route before saving.
curl -sf -X POST "$EMULATION_BASE_URL/fire" \\
  -H "Authorization: Bearer $EMULATION_CONTROL_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d "{\\"pluginId\\": \\"slack\\", \\"eventId\\": \\"$INPUT_EVENT\\", \\"params\\": $INPUT_PARAMS, \\"targetPath\\": \\"/integrations/slack/events\\"}"`,
  },
  {
    slug: 'slack-recorded-messages',
    label: 'Read messages the app posted to Slack',
    description:
      'Return the messages the app sent via chat.postMessage since boot (or the last seed/reset) for assertions.',
    inputSchema: { type: 'object', properties: {} },
    outputSchema: {
      type: 'object',
      properties: {
        messages: { type: 'array', description: 'Posted messages: { channel, text, ts, thread_ts?, user }' },
      },
    },
    scaffoldScript: `# Read messages the app posted to the emulated Slack.
curl -sf "$EMULATION_BASE_URL/plugins/slack/posted-messages" \\
  -H "Authorization: Bearer $EMULATION_CONTROL_TOKEN"`,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Plugin
// ─────────────────────────────────────────────────────────────────────────────

export const slackPlugin: EmulationPlugin<SlackEmuConfig, SlackFixture, SlackState> = {
  id: 'slack',
  name: 'Slack API',
  version: '1.0.0',

  domains: ['slack.com'],
  needsBrowserIntercept: true,

  fixtureSchema: slackFixtureSchema,

  envContract: [
    {
      logicalKey: 'signingSecret',
      strategy: 'random-hex',
      bytes: 32,
      sensitive: true,
      description: "Signs inbound webhooks — map to your app's Slack signing-secret env var",
    },
    {
      logicalKey: 'botToken',
      strategy: 'random-hex',
      bytes: 16,
      prefix: 'xoxb-emulated-',
      sensitive: true,
      description: 'Bot token the emulator accepts — issued by oauth.v2.access, or map it directly',
    },
    {
      logicalKey: 'clientId',
      strategy: 'static',
      staticValue: '1234567890.0987654321',
      description: "OAuth client id — map to your app's Slack client-id env var",
    },
    {
      logicalKey: 'clientSecret',
      strategy: 'static',
      staticValue: 'emulated-client-secret',
      sensitive: true,
      description: "OAuth client secret — map to your app's Slack client-secret env var",
    },
  ],

  inboundEvents,
  actionInterfaces,

  controlReads: [
    {
      path: 'posted-messages',
      handler: (_req, ctx: SlackCtx) => ({
        status: 200,
        body: { messages: ctx.state.postedMessages },
      }),
    },
    {
      path: 'channels',
      handler: (_req, ctx: SlackCtx) => ({
        status: 200,
        body: { channels: Array.from(ctx.state.channels.values()) },
      }),
    },
  ],

  // ── Sandbox-side routes ──────────────────────────────────────────────────

  routes: [
    { method: 'POST', path: '/api/oauth.v2.access', handler: handleOAuthAccess },
    { method: 'POST', path: '/api/auth.test', handler: handleAuthTest },
    { method: 'GET', path: '/api/conversations.list', handler: handleConversationsList },
    { method: 'POST', path: '/api/conversations.list', handler: handleConversationsList },
    { method: 'POST', path: '/api/chat.postMessage', handler: handlePostMessage },
    { method: 'POST', path: '/api/conversations.join', handler: handleConversationsJoin },
    { method: 'GET', path: '/api/conversations.history', handler: handleConversationsHistory },
    { method: 'GET', path: '/api/conversations.replies', handler: handleConversationsReplies },
    { method: 'GET', path: '/api/users.info', handler: handleUsersInfo },
    { method: 'POST', path: '/api/users.info', handler: handleUsersInfo },
    { method: 'GET', path: '/api/team.info', handler: handleTeamInfo },
    { method: 'POST', path: '/api/team.info', handler: handleTeamInfo },
    // Best-effort Assistant status — emulate the missing-scope soft failure
    {
      method: 'POST',
      path: '/api/assistant.threads.setStatus',
      handler: (req, ctx: SlackCtx) => requireBot(req, ctx) ?? slackError('missing_scope'),
    },
    // File uploads are not emulated — fail closed so nothing leaks to real Slack
    {
      method: 'GET',
      path: '/api/files.getUploadURLExternal',
      handler: (req, ctx: SlackCtx) => requireBot(req, ctx) ?? slackError('not_emulated'),
    },
    {
      method: 'POST',
      path: '/api/files.completeUploadExternal',
      handler: (req, ctx: SlackCtx) => requireBot(req, ctx) ?? slackError('not_emulated'),
    },
  ],

  // ── Browser-side routes ──────────────────────────────────────────────────

  getBrowserRoutes(_fixture) {
    return [
      {
        urlPattern: '**/slack.com/oauth/v2/authorize**',
        handler: async (route, _fix) => {
          const url = new URL(route.request().url());
          const redirectUri = url.searchParams.get('redirect_uri');
          const state = url.searchParams.get('state') || '';

          if (!redirectUri) {
            await route.continue();
            return;
          }

          const callbackUrl = new URL(redirectUri);
          callbackUrl.searchParams.set('code', `${EMULATED_CODE_PREFIX}${crypto.randomUUID()}`);
          if (state) callbackUrl.searchParams.set('state', state);

          await route.fulfill({
            status: 302,
            headers: { location: callbackUrl.toString() },
            body: '',
          });
        },
      },
    ];
  },

  // ── Initialization ───────────────────────────────────────────────────────

  initialize(config, fixture, env) {
    return {
      team: fixture.team,
      botUserId: fixture.botUserId,
      appId: config.appId ?? DEFAULT_APP_ID,
      channels: new Map(fixture.channels.map((c) => [c.id, c])),
      users: new Map(fixture.users.map((u) => [u.id, u])),
      postedMessages: [],
      botToken: env?.botToken ?? 'xoxb-emulated-fallback',
      tsCounter: 0,
    };
  },

  defaultFixture: {
    team: { id: 'T_EMULATED', name: 'Emulated Workspace', domain: 'emulated' },
    botUserId: 'U_BOT',
    channels: [{ id: 'C_GENERAL', name: 'general', is_private: false, is_member: false, is_archived: false }],
    users: [
      { id: 'U_BOT', name: 'canary-bot', real_name: 'Canary Bot', is_bot: true },
      { id: 'U_HUMAN', name: 'testuser', real_name: 'Test User', email: 'testuser@example.com', is_bot: false },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Route Handlers
// ─────────────────────────────────────────────────────────────────────────────

function handleOAuthAccess(req: EmulationRequest, ctx: SlackCtx): EmulationResponse {
  const params = new URLSearchParams(req.body ?? '');
  const code = params.get('code') ?? '';
  const clientId = params.get('client_id') ?? '';
  const clientSecret = params.get('client_secret') ?? '';

  if (ctx.env.clientId && clientId !== ctx.env.clientId) return slackError('invalid_client_id');
  if (ctx.env.clientSecret && clientSecret !== ctx.env.clientSecret) return slackError('bad_client_secret');
  if (!code.startsWith(EMULATED_CODE_PREFIX)) {
    ctx.log('warn', `OAuth exchange with non-emulated code: ${code}`);
    return slackError('invalid_code');
  }

  const firstHuman = Array.from(ctx.state.users.values()).find((u) => !u.is_bot);
  ctx.log('info', `Issued bot token for team ${ctx.state.team.id}`);

  return {
    status: 200,
    body: {
      ok: true,
      access_token: ctx.state.botToken,
      token_type: 'bot',
      scope: EMULATED_BOT_SCOPES,
      bot_user_id: ctx.state.botUserId,
      app_id: ctx.state.appId,
      team: { id: ctx.state.team.id, name: ctx.state.team.name },
      authed_user: { id: firstHuman?.id ?? 'U_HUMAN' },
    },
  };
}

function handleAuthTest(req: EmulationRequest, ctx: SlackCtx): EmulationResponse {
  const denied = requireBot(req, ctx);
  if (denied) return denied;

  return {
    status: 200,
    body: {
      ok: true,
      url: `https://${ctx.state.team.domain ?? 'emulated'}.slack.com/`,
      team: ctx.state.team.name,
      user: 'canary-bot',
      team_id: ctx.state.team.id,
      user_id: ctx.state.botUserId,
      bot_id: 'B_EMULATED',
    },
  };
}

function handleConversationsList(req: EmulationRequest, ctx: SlackCtx): EmulationResponse {
  const denied = requireBot(req, ctx);
  if (denied) return denied;

  const excludeArchived = req.url.searchParams.get('exclude_archived') === 'true';
  const channels = Array.from(ctx.state.channels.values()).filter(
    (c) => !excludeArchived || !c.is_archived
  );

  return {
    status: 200,
    body: {
      ok: true,
      channels: channels.map((c) => ({
        id: c.id,
        name: c.name,
        is_channel: true,
        is_private: c.is_private ?? false,
        is_member: c.is_member ?? false,
        is_archived: c.is_archived ?? false,
      })),
      response_metadata: { next_cursor: '' },
    },
  };
}

function handlePostMessage(req: EmulationRequest, ctx: SlackCtx): EmulationResponse {
  const denied = requireBot(req, ctx);
  if (denied) return denied;

  const parsed = safeParseJson(req.body);
  if (!parsed.ok) return parsed.error;
  const { channel, text } = parsed.data;
  if (!channel) return slackError('channel_not_found');

  const ts = mintTs(ctx.state);
  const message: PostedMessage = {
    channel: String(channel),
    text: String(text ?? ''),
    ts,
    user: ctx.state.botUserId,
    ...(parsed.data.thread_ts ? { thread_ts: String(parsed.data.thread_ts) } : {}),
    ...(parsed.data.blocks ? { blocks: parsed.data.blocks } : {}),
    ...(parsed.data.attachments ? { attachments: parsed.data.attachments } : {}),
  };
  ctx.state.postedMessages.push(message);
  ctx.log('info', `chat.postMessage → ${message.channel}: ${message.text.slice(0, 80)}`);

  return {
    status: 200,
    body: {
      ok: true,
      channel: message.channel,
      ts,
      message: { type: 'message', text: message.text, user: ctx.state.botUserId, ts },
    },
  };
}

function handleConversationsJoin(req: EmulationRequest, ctx: SlackCtx): EmulationResponse {
  const denied = requireBot(req, ctx);
  if (denied) return denied;

  const parsed = safeParseJson(req.body);
  if (!parsed.ok) return parsed.error;
  const channelId = String(parsed.data.channel ?? '');
  const channel = ctx.state.channels.get(channelId);
  if (!channel) return slackError('channel_not_found');

  channel.is_member = true;
  return {
    status: 200,
    body: { ok: true, channel: { id: channel.id, name: channel.name, is_member: true } },
  };
}

/** Serve message lookups from postedMessages (parents only). */
function handleConversationsHistory(req: EmulationRequest, ctx: SlackCtx): EmulationResponse {
  const denied = requireBot(req, ctx);
  if (denied) return denied;

  const channel = req.url.searchParams.get('channel') ?? '';
  const latest = req.url.searchParams.get('latest');
  let messages = ctx.state.postedMessages.filter((m) => m.channel === channel && !m.thread_ts);
  if (latest) messages = messages.filter((m) => m.ts === latest);

  return {
    status: 200,
    body: {
      ok: true,
      messages: messages.map((m) => ({ type: 'message', user: m.user, text: m.text, ts: m.ts })),
      has_more: false,
    },
  };
}

function handleConversationsReplies(req: EmulationRequest, ctx: SlackCtx): EmulationResponse {
  const denied = requireBot(req, ctx);
  if (denied) return denied;

  const channel = req.url.searchParams.get('channel') ?? '';
  const ts = req.url.searchParams.get('ts') ?? '';
  const thread = ctx.state.postedMessages.filter(
    (m) => m.channel === channel && (m.ts === ts || m.thread_ts === ts)
  );

  return {
    status: 200,
    body: {
      ok: true,
      messages: thread.map((m) => ({
        type: 'message',
        user: m.user,
        text: m.text,
        ts: m.ts,
        ...(m.thread_ts ? { thread_ts: m.thread_ts } : {}),
      })),
      has_more: false,
    },
  };
}

function handleUsersInfo(req: EmulationRequest, ctx: SlackCtx): EmulationResponse {
  const denied = requireBot(req, ctx);
  if (denied) return denied;

  const userId = readParam(req, 'user') ?? '';
  const user = ctx.state.users.get(userId);
  if (!user) return slackError('user_not_found');

  return {
    status: 200,
    body: {
      ok: true,
      user: {
        id: user.id,
        name: user.name,
        real_name: user.real_name ?? user.name,
        is_bot: user.is_bot ?? false,
        deleted: false,
        profile: {
          display_name: user.name,
          real_name: user.real_name ?? user.name,
          ...(user.email ? { email: user.email } : {}),
        },
      },
    },
  };
}

function handleTeamInfo(req: EmulationRequest, ctx: SlackCtx): EmulationResponse {
  const denied = requireBot(req, ctx);
  if (denied) return denied;

  return {
    status: 200,
    body: {
      ok: true,
      team: {
        id: ctx.state.team.id,
        name: ctx.state.team.name,
        domain: ctx.state.team.domain ?? 'emulated',
      },
    },
  };
}
